import { getGameAssetUrl } from './assets'

/**
 * Static snapshot of the games shown in marketing surfaces (the landing
 * "supported games" row and the header GAMES dropdown). Captured 2026-06-25
 * from the `games` table (`select id, name, display_name from games order by
 * display_name asc`).
 *
 * These surfaces intentionally do NOT fetch games live: it's stable marketing
 * content, and the live fetch (a) forced the landing into dynamic rendering and
 * (b) silently rendered "No games available" whenever the client Supabase env
 * wasn't present at runtime. A static list is reliable and avoids the round-trip.
 * Icons are still served from the CloudFront CDN via getGameAssetUrl.
 *
 * `slug` mirrors `getAllGames()` (slug === name) so /games/[slug] links match.
 * To refresh: re-run the query above and update the SNAPSHOT list below.
 */
export interface LandingGame {
  id: string
  name: string
  slug: string
  display_name: string
  /** CloudFront CDN URL for the game icon. */
  image: string
}

const SNAPSHOT: ReadonlyArray<{ id: string; name: string; display_name: string }> = [
  { id: '0e2a6efc-3ebd-47d7-8f3d-0d9b605550ea', name: 'apex-legends', display_name: 'Apex Legends' },
  { id: '9582d4d1-7a53-4ef3-8655-e56353fabccc', name: 'arc-raiders', display_name: 'Arc Raiders' },
  { id: 'c9df7576-95c4-4ba3-9540-5f54363f3ecf', name: 'battlefield-6', display_name: 'Battlefield 6' },
  { id: '74f282e8-e5a8-48d2-8ebc-e5e1cb882d46', name: 'call-of-duty', display_name: 'Call of Duty' },
  { id: '8d6b39ec-e9ab-422e-90aa-f2cd6540671b', name: 'escape-from-tarkov', display_name: 'Escape from Tarkov' },
  { id: '44e954c7-afcc-495f-ad96-e231542ba820', name: 'dissidia-duellum-final-fantasy', display_name: 'Final Fantasy' },
  { id: 'f146f0a7-3201-461a-9de4-204e944d7a8e', name: 'for-honor', display_name: 'For Honor' },
  { id: '67b75c12-f3e5-4a91-be1b-07b024d16735', name: 'fortnite', display_name: 'Fortnite' },
  { id: 'b5c05ca3-2922-4b66-86ed-c5686086c76a', name: 'gears-of-war', display_name: 'Gears of War' },
  { id: 'a0b8c72a-d4c3-407b-b1b2-9f5c474a222d', name: 'halo', display_name: 'Halo' },
  { id: '8eedc327-0358-4350-8a5a-cdb4199634a7', name: 'league-of-legends', display_name: 'League of Legends' },
  { id: '3191f74d-443b-473f-a5c0-9e88c42dc6df', name: 'marathon', display_name: 'Marathon' },
  { id: '6c2caabe-d109-4910-b389-a275a4957d66', name: 'marvel-rivals', display_name: 'Marvel Rivals' },
  { id: '3784ee18-99a2-423c-b5f9-45427857a61d', name: 'nba-2k', display_name: 'NBA 2K' },
  { id: '2ae5309d-54fb-4e50-9470-be11637e59ae', name: 'overwatch-2', display_name: 'Overwatch 2' },
  { id: 'cb8b9ef2-fcd1-402c-a4fd-461151de9c95', name: 'pubg', display_name: 'PUBG' },
  { id: '900d39c8-9bee-43a1-aad4-ae5fdf233bc7', name: 'rocket-league', display_name: 'Rocket League' },
  { id: '9e5bd485-b9e6-44f2-8376-62dc81984582', name: 'super-smash-bros', display_name: 'Super Smash Bros' },
  { id: '6a430c57-83cd-4024-a62a-71148bb844ee', name: 'valorant', display_name: 'Valorant' },
]

export const LANDING_GAMES: ReadonlyArray<LandingGame> = SNAPSHOT.map((game) => ({
  ...game,
  slug: game.name,
  image: getGameAssetUrl(game.display_name),
}))
